import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { documentsApi, commentsApi, requestsApi } from '../lib/api';
import { formatDate, isImageFilename } from '../lib/utils';
import { Loader2, MessageSquare, ArrowRight, FileText, Download, CheckCircle2, Bell, Layers, BadgeCheck } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

/**
 * Unified Document Preview Modal.
 *
 * Always shows: file viewer + metadata (Type, Status, Version, Uploader, Uploaded, Assigned to)
 *              + latest 3 discussion comments + Download + Go to discussion + Close.
 *
 * Action visibility is **context-aware**:
 *  - "Approve document" → only when the current user is in `doc.status_assignees`
 *    AND `doc.status === 'PENDING_REVIEW'`.
 *  - "Nudge" → only when `doc.status === 'PENDING_REVIEW'` AND the doc has *other*
 *    assignees AND the current user is Chairman/Admin (mirrors the gate's nudge rule).
 *
 * To prevent stale "—" fields when the caller only knows a doc id, the modal always
 * refreshes the doc record from the backend on open.
 */
export default function DocumentPreviewModal({
  open,
  onOpenChange,
  doc: initialDoc,        // partial doc is fine; modal will fetch the full record
  requestId,               // optional — used to enable Nudge (request-scoped endpoint)
  allowNudge = false,     // gate sets this to true; Documents tab leaves it false
  onGoToThread = null,    // (thread_id) => void
  onApproved = null,      // (doc_id) => void — called after a successful approve
}) {
  const { user, isCompanyRep } = useAuth() || { user: null, isCompanyRep: () => false };
  const [doc, setDoc] = useState(initialDoc);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewErr, setPreviewErr] = useState(null);
  const [threadSnippet, setThreadSnippet] = useState([]);
  const [snippetLoading, setSnippetLoading] = useState(false);
  const [approving, setApproving] = useState(false);
  const [nudging, setNudging] = useState(false);

  // The modal's "active doc" — starts at initialDoc, but can be swapped by clicking
  // a bundle-content row to dive into a source doc without closing the modal.
  const [activeDocId, setActiveDocId] = useState(initialDoc?.id || null);

  useEffect(() => {
    setActiveDocId(initialDoc?.id || null);
    setDoc(initialDoc);
  }, [initialDoc?.id, initialDoc]);

  useEffect(() => {
    if (!open || !activeDocId) return;
    let cancelled = false;
    setPreviewUrl(null);
    setPreviewErr(null);

    // (a) Always refresh the doc record so Type / Uploader / Uploaded / Assigned-to are correct
    documentsApi.getById(activeDocId)
      .then(res => { if (!cancelled) setDoc((prev) => ({ ...(prev || {}), ...res.data })); })
      .catch(() => { /* keep current */ });

    // (b) Preview URL
    setPreviewLoading(true);
    documentsApi.getPreviewUrl(activeDocId)
      .then(res => {
        if (cancelled) return;
        const backend = process.env.REACT_APP_BACKEND_URL || '';
        setPreviewUrl(`${backend}${res.data.preview_url}`);
      })
      .catch(e => { if (!cancelled) setPreviewErr(e?.response?.data?.detail || 'Could not load preview'); })
      .finally(() => { if (!cancelled) setPreviewLoading(false); });

    // (c) Latest comments
    setSnippetLoading(true);
    commentsApi.getByDocument(activeDocId)
      .then(res => {
        if (cancelled) return;
        const arr = Array.isArray(res.data) ? res.data : [];
        // Backend returns a TREE: roots + each root's replies nested in `replies`.
        // Flatten so the snippet sees real user replies instead of only the root.
        const flat = [];
        for (const root of arr) {
          flat.push(root);
          for (const r of (root.replies || [])) flat.push(r);
        }
        const filtered = flat.filter(c => (
          !c.is_document_thread &&
          !c.is_assignment_change &&
          !c.is_version_marker &&
          !c.is_status_change &&
          !c.is_signed_marker
        ));
        filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setThreadSnippet(filtered.slice(0, 3));
      })
      .catch(() => { if (!cancelled) setThreadSnippet([]); })
      .finally(() => { if (!cancelled) setSnippetLoading(false); });

    return () => { cancelled = true; };
  }, [open, activeDocId]);

  if (!doc) return null;

  const isImg = isImageFilename(doc.filename);
  const isPdf = doc.filename?.toLowerCase().endsWith('.pdf');
  // iter111 — Office Online Viewer for Word/Excel/PowerPoint documents
  const lcName = (doc.filename || '').toLowerCase();
  const isOfficeDoc = ['.docx', '.doc', '.pptx', '.ppt', '.xlsx', '.xls'].some((ext) => lcName.endsWith(ext));
  const isSigned = (doc.doc_type || '').startsWith('Signed_') || (doc.filename || '').startsWith('Signed_');
  const isBundleSigned = isSigned && (doc.doc_type || '').endsWith('_Signature_Bundle');
  // iter109: any doc with bundle_contents qualifies for the "Bundle contents" section,
  // including unsigned generated bundles (doc.source_document_ids set).
  const showBundleContents = Array.isArray(doc.bundle_contents) && doc.bundle_contents.length > 0;
  // iter109: attachment-context preview (an Attachment doc rendered in the modal)
  const isAttachmentDoc = !!doc.is_thread_attachment || (doc.doc_type === 'Attachment');

  // Context-aware permissions
  const canApprove =
    !!user &&
    doc.status === 'PENDING_REVIEW' &&
    (
      // assigned reviewer
      (Array.isArray(doc.status_assignees) && doc.status_assignees.includes(user.id)) ||
      // OR universal authority — Chairman / Admin / CRD always allowed (matches backend)
      ['CHAIRMAN', 'ADMIN', 'CRD'].includes(user.role)
    );

  const canNudge =
    allowNudge &&
    !!user &&
    doc.status === 'PENDING_REVIEW' &&
    Array.isArray(doc.status_assignees) &&
    doc.status_assignees.length > 0 &&
    !doc.status_assignees.every((uid) => uid === user.id) &&
    ['CHAIRMAN', 'ADMIN'].includes(user.role) &&
    !!requestId;

  const assigneeNames = Array.isArray(doc.assignee_names) && doc.assignee_names.length
    ? doc.assignee_names.map((a) => (typeof a === 'string' ? a : `${a.name}${a.role ? ` (${a.role})` : ''}`))
    : null;

  const handleApprove = async () => {
    if (approving) return;
    setApproving(true);
    try {
      await documentsApi.updateStatus(doc.id, 'APPROVED');
      toast.success(`${doc.filename} approved`);
      setDoc(prev => ({ ...prev, status: 'APPROVED' }));
      onApproved?.(doc.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to approve');
    } finally {
      setApproving(false);
    }
  };

  const handleNudge = async () => {
    if (nudging || !requestId) return;
    setNudging(true);
    try {
      const res = await requestsApi.nudgeDocAssignees(requestId, doc.id);
      const nudged = res?.data?.nudged?.length || 0;
      const throttled = res?.data?.throttled?.length || 0;
      if (nudged > 0) toast.success(`Reminder sent to ${nudged} assignee${nudged === 1 ? '' : 's'}`);
      else if (throttled > 0) toast.info('Already nudged recently — please wait a few minutes');
      else toast.info('Nothing to nudge');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not nudge');
    } finally {
      setNudging(false);
    }
  };

  const handleDownload = async () => {
    try {
      const res = await documentsApi.getDownloadUrl(doc.id);
      const backend = process.env.REACT_APP_BACKEND_URL || '';
      // Hidden-anchor approach — window.open() is blocked by browsers when called
      // after an awaited promise resolves (it's no longer a direct user gesture).
      const link = document.createElement('a');
      link.href = `${backend}${res.data.download_url}`;
      if (doc.filename) link.setAttribute('download', doc.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      toast.error('Could not start download');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl w-[95vw] max-h-[94vh] overflow-hidden p-0 border-0 shadow-2xl" data-testid="doc-preview-modal">
        {/* iter113 — Bold gradient header (slate→indigo→blue) with the filename, doc
            type and version chips inline. Pulls visual weight to the top while leaving
            the preview pane uncluttered. */}
        <DialogHeader className="relative px-5 py-3 bg-gradient-to-r from-slate-900 via-indigo-900 to-blue-900 text-white overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(99,102,241,0.35),transparent_60%)] pointer-events-none" />
          <div className="relative flex items-center gap-2.5 pr-8">
            <div className="w-8 h-8 rounded-lg bg-white/15 backdrop-blur flex items-center justify-center ring-1 ring-white/20 shrink-0">
              <FileText className="w-4 h-4 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <DialogTitle className="text-sm font-semibold text-white truncate" title={doc.filename}>
                {doc.filename}
              </DialogTitle>
              <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                {doc.doc_type && (
                  <Badge className="text-[9px] h-4 bg-white/15 text-white border border-white/20 px-1.5">
                    {doc.doc_type}
                  </Badge>
                )}
                <Badge className="text-[9px] h-4 bg-white/15 text-white border border-white/20 px-1.5">
                  v{doc.version ?? '?'}
                </Badge>
                {showBundleContents && (
                  <Badge className="text-[9px] h-4 bg-amber-400/25 text-amber-100 border border-amber-300/30 px-1.5 gap-0.5">
                    <Layers className="w-2.5 h-2.5" /> Bundle · {doc.bundle_contents.length}
                  </Badge>
                )}
                {isSigned && (
                  <Badge className="text-[9px] h-4 bg-emerald-400/25 text-emerald-100 border border-emerald-300/30 px-1.5 gap-0.5">
                    <BadgeCheck className="w-2.5 h-2.5" /> Signed
                  </Badge>
                )}
              </div>
            </div>
          </div>
          <DialogDescription className="sr-only">
            Document preview with metadata, latest discussion and quick actions
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-0">
          {/* Preview pane — iter113: wider + taller, scroll inside */}
          <div className="min-h-[65vh] max-h-[78vh] bg-slate-100 border-b lg:border-b-0 lg:border-r border-slate-200 flex items-center justify-center overflow-auto">
            {previewLoading && (
              <div className="flex flex-col items-center gap-2 text-slate-500">
                <Loader2 className="w-6 h-6 animate-spin" />
                <span className="text-sm">Loading preview…</span>
              </div>
            )}
            {!previewLoading && previewErr && (
              <div className="text-center text-slate-500 text-sm p-6">
                <p>{previewErr}</p>
                <Button variant="outline" size="sm" className="mt-3" onClick={handleDownload} data-testid="doc-preview-fallback-download">
                  <Download className="w-4 h-4 mr-2" /> Download instead
                </Button>
              </div>
            )}
            {!previewLoading && !previewErr && previewUrl && (
              isImg ? (
                <img src={previewUrl} alt={doc.filename} className="max-w-full max-h-[70vh] object-contain" data-testid="doc-preview-image" />
              ) : isPdf ? (
                <iframe src={previewUrl} title={doc.filename} className="w-full h-[70vh]" data-testid="doc-preview-iframe" />
              ) : isOfficeDoc ? (
                /* iter111 — Microsoft Office Online viewer renders DOCX/DOC/PPTX/XLSX
                   inline. It requires the URL to be publicly reachable, which our
                   token-signed download URL is. */
                <iframe
                  src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(previewUrl)}`}
                  title={doc.filename}
                  className="w-full h-[70vh] bg-white"
                  data-testid="doc-preview-office-iframe"
                />
              ) : (
                <div className="text-center text-slate-500 text-sm p-6">
                  <p className="mb-3">Inline preview is not available for this file type.</p>
                  <Button variant="outline" size="sm" onClick={handleDownload}>
                    <Download className="w-4 h-4 mr-2" /> Download to view
                  </Button>
                </div>
              )
            )}
          </div>

          {/* Sticky metadata sidebar (iter113) — sections always visible while
              the preview pane scrolls independently. */}
          <div className="p-5 space-y-4 overflow-y-auto max-h-[78vh] bg-white sticky top-0">
            <div className="space-y-2 text-sm">
              <MetaRow label="Type">
                <Badge className="bg-slate-100 text-slate-700 font-medium">{doc.doc_type || '—'}</Badge>
              </MetaRow>
              {/* iter112 — STATUS row removed per spec (doc-thread status is shown in the
                  thread header, not redundantly here). */}
              <MetaRow label="Version">v{doc.version ?? '?'}</MetaRow>
              <MetaRow label="Uploader">
                {doc.uploader_name || '—'}
                {doc.uploader_role && <span className="text-slate-400 ml-1">({doc.uploader_role})</span>}
              </MetaRow>
              <MetaRow label="Uploaded">{doc.uploaded_at ? formatDate(doc.uploaded_at) : '—'}</MetaRow>
              {!isSigned && !isCompanyRep() && (
                <MetaRow label="Assigned to">
                  {assigneeNames
                    ? assigneeNames.join(', ')
                    : (doc.status_assignees?.length ? `${doc.status_assignees.length} reviewer(s)` : '—')}
                </MetaRow>
              )}
            </div>

            {/* Signatories — only for signed docs */}
            {isSigned && (
              <div className="pt-3 border-t border-slate-100">
                <p className="text-xs uppercase tracking-wide font-semibold text-emerald-700 mb-2 flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Signatories
                </p>
                {(doc.signatories && doc.signatories.length > 0) ? (
                  <ul className="space-y-2" data-testid="doc-preview-signatories">
                    {doc.signatories.map((s, i) => (
                      <li key={i} className="text-xs border-l-2 border-emerald-300 pl-2 py-0.5">
                        <p className="font-medium text-slate-800">
                          {s.typed_name || s.name || '—'}
                          {s.role && <span className="text-slate-400 ml-1">({s.role})</span>}
                        </p>
                        {s.signed_at && (
                          <p className="text-slate-500">{formatDate(s.signed_at)}</p>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-xs text-slate-400 italic">No signatories on record</div>
                )}
              </div>
            )}

            {/* Bundle contents — both signed and generated bundles (iter109) */}
            {showBundleContents && (
              <div className="pt-3 border-t border-slate-100">
                <p className={`text-xs uppercase tracking-wide font-semibold mb-2 flex items-center gap-1.5 ${isBundleSigned ? 'text-emerald-700' : 'text-indigo-700'}`}>
                  <FileText className="w-3.5 h-3.5" /> Bundle contents ({doc.bundle_contents.length})
                </p>
                <ul className="space-y-1" data-testid="doc-preview-bundle-contents">
                  {doc.bundle_contents.map((b) => (
                    <li key={b.id}>
                      <button
                        type="button"
                        onClick={() => setActiveDocId(b.id)}
                        className={`w-full text-left text-xs border-l-2 pl-2 py-1 rounded transition-colors ${isBundleSigned ? 'border-emerald-300 hover:bg-emerald-50' : 'border-indigo-300 hover:bg-indigo-50'}`}
                        data-testid={`doc-preview-bundle-item-${b.id}`}
                        title="Click to preview this source document"
                      >
                        <p className="font-medium text-slate-800 truncate">{b.filename}</p>
                        <p className="text-slate-500">{b.doc_type} · v{b.version}</p>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Latest discussion — hidden for signed docs (no real thread to preview).
                iter160 — Also hidden for Company / Company Rep to avoid leaking
                reviewer names/comments through the preview side panel. */}
            {!isSigned && !isCompanyRep() && (
              <div className="pt-3 border-t border-slate-100">
                <p className="text-xs uppercase tracking-wide font-semibold text-slate-500 mb-2 flex items-center gap-1.5">
                  <MessageSquare className="w-3.5 h-3.5" /> Latest discussion
                </p>
                {snippetLoading ? (
                  <div className="text-xs text-slate-400">Loading…</div>
                ) : threadSnippet.length === 0 ? (
                  <div className="text-xs text-slate-400 italic">No comments yet</div>
                ) : (
                  <ul className="space-y-2" data-testid="doc-preview-thread-snippet">
                    {threadSnippet.map(c => (
                      <li key={c.id} className="text-xs border-l-2 border-slate-200 pl-2 py-0.5">
                        <div className="flex items-baseline justify-between gap-2">
                          <span className="font-medium text-slate-700 truncate">{c.user_name}</span>
                          <span className="text-slate-400 shrink-0">{formatDate(c.created_at)}</span>
                        </div>
                        <p className="text-slate-600 mt-0.5 line-clamp-2">{c.content || <em className="text-slate-400">(attachment)</em>}</p>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {canNudge && (
              <div className="pt-3 border-t border-slate-100" data-testid="doc-preview-actions">
                {/* iter113 — Approve button removed per spec. Nudge stays as a gate-only
                    soft prompt. */}
                <Button
                  variant="outline"
                  className="w-full border-amber-400 text-amber-700 hover:bg-amber-50"
                  onClick={handleNudge}
                  disabled={nudging}
                  data-testid="doc-preview-nudge-btn"
                >
                  {nudging ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bell className="w-4 h-4 mr-2" />}
                  Nudge reviewers
                </Button>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="px-6 py-3 border-t border-slate-200 bg-slate-50">
          <Button variant="ghost" onClick={handleDownload} data-testid="doc-preview-download-btn">
            <Download className="w-4 h-4 mr-2" /> Download
          </Button>
          {doc.thread_id && onGoToThread && (
            <Button
              onClick={() => onGoToThread(doc.thread_id)}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="doc-preview-go-to-thread-btn"
            >
              Go to discussion <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="doc-preview-close-btn">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MetaRow({ label, children }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-xs uppercase tracking-wide text-slate-500 w-24 shrink-0">{label}</span>
      <span className="text-sm text-slate-800">{children}</span>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    APPROVED: 'bg-emerald-100 text-emerald-700',
    REJECTED: 'bg-red-100 text-red-700',
    PENDING_REVIEW: 'bg-amber-100 text-amber-800',
  };
  return <Badge className={map[status] || 'bg-slate-100 text-slate-700'}>{status || '—'}</Badge>;
}
